import React, { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import client from "@culturehq/client";
import { classnames, Icon, PlainButton } from "@culturehq/components";

import noStories from "images/no-stories.png";

import { getImageFromStory, t, Trans } from "app/utils";
import imagesSizes from "constants/imagesSizes";
import EmptySlider from "components/admin/settings/EmptySlider";

import styles from "./trend-slider.module.scss";

const StoryTrendChildImage = ({
  childAnswer, color, index, orgName
}) => {
  if (index > 1) {
    return <></>;
  }

  return (
    <div className="Trends-Slider__stories-image" style={{ backgroundImage: `url(${getImageFromStory(imagesSizes.SMALL, childAnswer)})` }}>
      {index === 1 && (
        <div
          className="Overlay"
          style={{ backgroundColor: orgName === "activisionblizzard" || orgName === "activision blizzard" ? `${color}99` : "rgba(0, 0, 0, 0.6)" }}
        >
          <p><Trans>View stories</Trans></p>
        </div>
      )}
    </div>
  );
};

const initialState = {
  appending: null,
  error: null,
  fetching: true,
  pagination: {},
  trends: []
};

const reducer = (state, action) => {
  switch (action.type) {
    case "appendStart":
      return { ...state, appending: state.pagination.currentPage + 1 };
    case "appendSuccess":
      return {
        ...state,
        appending: null,
        pagination: action.response.pagination,
        trends: [...state.storyQuestions, ...action.response.storyQuestions]
      };
    case "appendError":
      return { ...state, appending: null, error: action.error };
    case "fetchStart":
      return { ...state, error: null, fetching: true };
    case "fetchSuccess":
      return {
        ...state,
        error: null,
        fetching: false,
        pagination: action.response.pagination,
        trends: action.response.storyQuestions
      };
    case "fetchError":
      return { ...state, error: action.error, fetching: false };
    default:
      return state;
  }
};

const useFetchTrends = (dispatch, queryParams, organization, preview) => useEffect(
  () => {
    if (!preview) {
      let cancelled = false;
      dispatch({ type: "fetchStart" });

      client.listOrganizationLandingPagesStoryTrends({
        ...queryParams,
        organization
      })
        .then(response => {
          if (!cancelled) {
            if (organization === "boston scientific") {
              dispatch({
                type: "fetchSuccess",
                response: {
                  ...response,
                  storyQuestions: response.storyQuestions.sort((first, second) => {
                    // Move the object with id = 132966 to the first position
                    if (first.id === 132966) return -1;
                    if (second.id === 132966) return 1;
                    // Keep the others in the same order
                    return 0;
                  })
                }
              });
            } else {
              dispatch({ type: "fetchSuccess", response });
            }
          }
        })
        .catch(error => {
          if (!cancelled) {
            dispatch({ type: "fetchError", error });
          }
        });

      return () => {
        cancelled = true;
      };
    }
    dispatch({ type: "fetchError", error: "" });

    return undefined;
  },
  [dispatch, queryParams, organization, preview]
);

const useAppendTrends = (dispatch, appending, queryParams, organization, preview) => useEffect(
  () => {
    if (appending && !preview) {
      let cancelled = false;

      client.listOrganizationLandingPagesStoryTrends({
        ...queryParams,
        page: appending,
        organization
      })
        .then(response => {
          if (!cancelled) {
            dispatch({ type: "appendSuccess", response });
          }
        })
        .catch(error => {
          if (!cancelled) {
            dispatch({ type: "appendError", error });
          }
        });

      return () => {
        cancelled = true;
      };
    }

    return undefined;
  },
  [dispatch, appending, queryParams, organization, preview]
);

const getSlideLayout = (
  index,
  containerRef,
  sliderRef,
  size
) => {
  const TREND_WIDTH = containerRef.current?.offsetWidth;
  const PADDING = 15;
  let percent = index * (100 / (size));
  const left = index !== 0;
  let right = index !== size && size > 1;

  const container = containerRef.current;
  const slider = sliderRef.current;
  const sliderWidth = (TREND_WIDTH * size) + (PADDING * (size));

  if (!(container instanceof HTMLDivElement) || !(slider instanceof HTMLDivElement)) {
    return { percent, left, right };
  }

  const pixels = (percent * sliderWidth) / 100;
  const correction = pixels - (slider.clientWidth - container.clientWidth);

  if ((index + 1) * (100 / (size)) >= 100) {
    right = false;
  }

  if (correction > 0) {
    percent -= (correction / sliderWidth) * 100;
  }

  return { percent, left, right };
};

const StoryTrends = ({
  admin = false, color, organization, orgName, preview = false, queryParams
}) => {
  const containerRef = useRef();
  const sliderRef = useRef();
  const [state, dispatch] = useReducer(reducer, initialState);

  useFetchTrends(dispatch, queryParams, organization, preview);
  useAppendTrends(dispatch, state.appending, queryParams, organization, preview);

  const history = useHistory();
  const [index, setIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const [slideLayout, setSlideLayout] = useState({ percent: 0, left: false, right: false });
  const onNext = () => setIndex(value => value + 1);
  const onPrev = () => setIndex(value => value - 1);

  useLayoutEffect(() => {
    const adjustCardSize = () => {
      setCardWidth(containerRef.current?.offsetWidth);
    };
    window.addEventListener("resize", adjustCardSize);
    return () => window.removeEventListener("resize", adjustCardSize);
  }, [containerRef]);

  useEffect(
    () => {
      if (containerRef.current) {
        setCardWidth(containerRef.current?.offsetWidth);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [containerRef.current]
  );

  useEffect(
    () => {
      setSlideLayout(getSlideLayout(index, containerRef, sliderRef, state.trends.length));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [containerRef.current, sliderRef.current, index, state.trends.length]
  );

  const handleTrendClick = trend => {
    if (admin) {
      return;
      // history.push(`/admin/landing-page/preview/users/${trend.id}`);
      // return;
    }

    history.push(`/trends/${trend.id}`);
  };

  if (state.fetching) {
    return <EmptySlider />;
  }

  return (
    <div className="Preview-Browser__Trends">
      {state.trends.length > 0 ? (
        <div style={{ position: "relative" }}>
          <button
            aria-label={t("Previous")}
            type="button"
            className={classnames(styles.direction, styles.left)}
            onClick={onPrev}
            disabled={!slideLayout.left}
          >
            <Icon icon="chevron-left" />
          </button>
          <button
            aria-label={t("Next")}
            type="button"
            className={classnames(styles.direction, styles.right)}
            onClick={onNext}
            disabled={!slideLayout.right}
          >
            <Icon icon="chevron-right" />
          </button>
          <div ref={containerRef} className={`${state.trends.length === 0 ? styles.emptyContainer : styles.container} no-scrollbar`}>
            <div
              ref={sliderRef}
              className={`${styles.slider} ${state.trends.length === 0 ? styles.sliderEmpty : ""} Trends-Slider ${state.trends.length === 0 ? "Trends-Slider__Empty" : ""}`}
              style={{ transform: `translateX(-${slideLayout.percent}%)` }}
            >
              {state.trends.map(trend => (
                <PlainButton
                  aria-labelledby={trend.id}
                  className={`${styles.card}`}
                  key={trend.id}
                  style={{ width: cardWidth }}
                  onClick={() => handleTrendClick(trend)}
                >
                  <div id={trend.id} style={{ display: "none" }}>{trend.question}</div>
                  <div className="Trends-Slider__banner" style={{ backgroundImage: `url(${trend.imageUrl})` }}>
                    <div className="Trends-Slider__banner__overlay">
                      <p className={styles.sublabel}>
                        <Icon className={styles.sublabelIcon} icon="thunder-filled" />
                        <Trans>Story Trend</Trans>
                      </p>
                      <p className={styles.label}>{trend.question}</p>
                    </div>
                  </div>
                  <div className="Trends-Slider__stories">
                    {trend.childApprovedAnswers.map((childAnswer, index) => (
                      <StoryTrendChildImage
                        key={index}
                        childAnswer={childAnswer}
                        color={color}
                        index={index}
                        orgName={orgName}
                      />
                    ))}
                  </div>
                </PlainButton>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          {admin && (
            <div className="Stories__Empty">
              <img className="Stories__Empty--image" src={noStories} alt={t("Empty Stories")} />
              <div>
                <p className="Stories__Empty--paragraph">
                  <Trans>No stories have been approved for this trend yet.</Trans>
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StoryTrends;
